import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { useForm } from 'react-hook-form';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import { repositories } from 'api';
import { checkoutOperations } from 'store/ducks/checkout';
import { CartShopStats, ShopCartItem } from 'store/ducks/shopCart/types';
import { CheckoutValues } from 'store/ducks/checkout/types';
import { sumStatsInCartShopItems } from 'store/ducks/shopCart/selectors';
import Button from 'common/components/Button';
import { StoreBasicDataApiStructure } from 'common/types';
import { OrderSchema } from './common/validation';
import { routes } from '../../common/routes';
import { DEFAULT_GRID_CONTAINER_SPACING, CHANNEL } from 'common/constants';
import { shopCartOperations } from 'store/ducks/shopCart';
import FormPanel from './components/FormPanel';
import {
    FormPanelType,
    PAYMENT_METHOD,
    SHIPPING_FORM,
    PAYER_ADDRESS,
    BILLING_ADDRESS,
    SHIPPING_ADDRESS,
    REALIZATION_DATE,
    NOTES,
    CUSTOMER_EMAIL,
} from '../../common/constants';
import { CheckoutType } from 'api/repositories';
import DifferentData from './components/DifferentData';
import CheckoutRepository from 'api/repositories/Checkout';
import { getCartToken } from '../../common/functions';

type Props = {
    carts: any;
    checkoutData: StoreBasicDataApiStructure<CheckoutType>;
    checkoutValues: CheckoutValues;
    fetchCheckoutData: Function;
    isPromoter: boolean;
    items: Array<ShopCartItem>;
    itemsStats: CartShopStats;
    removeItemFromShopCart: Function;
    setCheckoutValues: Function;
};

type MappingType = {
    me: StoreBasicDataApiStructure<repositories.MeType>;
    shopCart: Array<ShopCartItem>;
    simpleProduct: StoreBasicDataApiStructure<repositories.SimpleProductType>;
    checkout: {
        data: StoreBasicDataApiStructure<CheckoutType>;
        values: CheckoutValues;
    };
};

const Order: React.FC<Props> = ({
    carts,
    checkoutData,
    checkoutValues,
    fetchCheckoutData,
    isPromoter,
    items,
    setCheckoutValues,
}) => {
    const [differentData, setDifferentData] = useState(checkoutValues.differentData ?? true);
    const { push } = useHistory();

    useEffect(() => {
        if (!checkoutValues.orderType) {
            push(routes.CART);
        }
    }, []);

    const { control, errors, handleSubmit, setError } = useForm({
        validationSchema: OrderSchema(isPromoter, checkoutValues.orderType),
    });

    const cartToken = getCartToken(carts, CHANNEL);

    const errorsCount = Object.keys(errors).length;
    const errorsExist = useMemo(() => errorsCount > 0, [errorsCount]);
    const productsCart = items.length;

    const onSubmit = useCallback(
        (values) => {
            const api = new CheckoutRepository();

            setCheckoutValues({ ...checkoutValues, ...values });

            Promise.all([
                api.put({
                    data: { method: values[PAYMENT_METHOD] },
                    url: `checkout/${cartToken}/payment`,
                }),
                api.put({
                    data: { method: values[SHIPPING_FORM] },
                    url: `checkout/${cartToken}/shipping`,
                }),
                api.put({
                    data: {
                        [BILLING_ADDRESS]: values[BILLING_ADDRESS],
                        [PAYER_ADDRESS]: values[PAYER_ADDRESS] || values[BILLING_ADDRESS],
                        [SHIPPING_ADDRESS]: values[SHIPPING_ADDRESS],
                    },
                    url: `checkout/${cartToken}/address`,
                }),
                api.put({
                    data: {
                        code: values[REALIZATION_DATE],
                    },
                    url: `grant/checkout/${cartToken}/realization-date`,
                }),
                isPromoter
                    ? api.put({
                          data: {
                              email: values[CUSTOMER_EMAIL],
                              notes: values[NOTES],
                          },
                          url: `checkout/${cartToken}/promoter-data`,
                      })
                    : null,
            ])
                .then(() => push(routes.SUMMARY))
                .catch(({ response }) => {
                    const errors = response.data.errors;

                    Object.keys(errors).forEach((key) => {
                        setError(key, '', errors[key]);
                    });
                });
        },
        [checkoutValues, push, setCheckoutValues],
    );

    const handlePrevClick = () => push(routes.CART);

    return productsCart ? (
        <form onSubmit={handleSubmit(onSubmit)} style={{ margin: '2rem 0' }}>
            <Grid container spacing={DEFAULT_GRID_CONTAINER_SPACING}>
                <Grid item xs={12}>
                    <FormPanel
                        cartToken={cartToken}
                        checkoutData={checkoutData}
                        checkoutValues={checkoutValues}
                        control={control}
                        errors={errors}
                        fetchCheckoutData={fetchCheckoutData}
                        type={FormPanelType.payment}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormPanel
                        cartToken={cartToken}
                        checkoutData={checkoutData}
                        checkoutValues={checkoutValues}
                        control={control}
                        errors={errors}
                        fetchCheckoutData={fetchCheckoutData}
                        type={FormPanelType.realizationDate}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormPanel
                        cartToken={cartToken}
                        checkoutData={checkoutData}
                        checkoutValues={checkoutValues}
                        control={control}
                        errors={errors}
                        fetchCheckoutData={fetchCheckoutData}
                        type={FormPanelType.shipmentForm}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormPanel
                        checkoutValues={checkoutValues}
                        control={control}
                        differentData={differentData}
                        errors={errors}
                        type={FormPanelType.invoiceData}
                    />
                </Grid>
                <Grid item xs={12}>
                    <DifferentData
                        control={control}
                        differentData={differentData}
                        setCheckoutValues={setCheckoutValues}
                        setDifferentData={setDifferentData}
                    />
                </Grid>
                {differentData ? (
                    <Grid item xs={12}>
                        <FormPanel
                            checkoutValues={checkoutValues}
                            control={control}
                            errors={errors}
                            type={FormPanelType.recipientData}
                        />
                    </Grid>
                ) : null}
                <Grid item xs={12}>
                    <FormPanel
                        checkoutValues={checkoutValues}
                        control={control}
                        errors={errors}
                        type={FormPanelType.shipmentData}
                    />
                </Grid>
                {isPromoter ? (
                    <>
                        <Grid item xs={12}>
                            <FormPanel
                                checkoutValues={checkoutValues}
                                control={control}
                                errors={errors}
                                type={FormPanelType.customerEmail}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormPanel
                                checkoutValues={checkoutValues}
                                control={control}
                                errors={errors}
                                type={FormPanelType.notes}
                            />
                        </Grid>
                    </>
                ) : null}
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="space-between">
                        <Button
                            colorTheme="darkgray"
                            onClick={handlePrevClick}
                            size="large"
                            type="button"
                        >
                            Wstecz
                        </Button>
                        <Button disabled={errorsExist} size="large" type="submit">
                            Dalej
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </form>
    ) : null;
};

const mapStateToProps = (state: MappingType) => ({
    ...state.simpleProduct,
    carts: state.me.data?.carts,
    isPromoter: state.me.data?.isPromoter,
    items: state.shopCart,
    itemsStats: sumStatsInCartShopItems(state),
    checkoutData: state.checkout.data,
    checkoutValues: state.checkout.values,
});

const mapDispatchToProps = {
    changeProductQuantity: shopCartOperations.changeProductQuantity,
    removeItemFromShopCart: shopCartOperations.removeItemFromShopCart,
    fetchCheckoutData: (url: string) => checkoutOperations.fetchCheckoutData.request(url),
    setCheckoutValues: checkoutOperations.setCheckoutValues,
};

export default connect(mapStateToProps, mapDispatchToProps)(Order);
